/**
 * Deterministic random numbers and noise for the cloud generator.
 *
 * Everything here is a pure function of its inputs and the seed, built on 32-bit integer
 * hashing (Math.imul), so a scene generates the same clouds on every device and browser:
 * no Math.random, no platform-dependent transcendental functions in the hash.
 */

/** mulberry32: a small, fast, well-distributed 32-bit PRNG. Returns floats in [0, 1). */
export function createRng(seed: number): () => number {
    let a = (seed | 0) ^ 0x9e3779b9;
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Integer lattice hash → [0, 1). */
export function hash3(x: number, y: number, z: number, seed: number): number {
    let h = Math.imul(x | 0, 0x8da6b343) ^ Math.imul(y | 0, 0xd8163841) ^ Math.imul(z | 0, 0xcb1ab31f) ^ Math.imul(seed | 0, 0x165667b1);
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
    h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
    h ^= h >>> 15;
    return (h >>> 0) / 4294967296;
}

const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);

/** Value noise in [0, 1) with quintic interpolation. */
export function valueNoise3(x: number, y: number, z: number, seed: number): number {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const iz = Math.floor(z);
    const fx = fade(x - ix);
    const fy = fade(y - iy);
    const fz = fade(z - iz);
    const n000 = hash3(ix, iy, iz, seed);
    const n100 = hash3(ix + 1, iy, iz, seed);
    const n010 = hash3(ix, iy + 1, iz, seed);
    const n110 = hash3(ix + 1, iy + 1, iz, seed);
    const n001 = hash3(ix, iy, iz + 1, seed);
    const n101 = hash3(ix + 1, iy, iz + 1, seed);
    const n011 = hash3(ix, iy + 1, iz + 1, seed);
    const n111 = hash3(ix + 1, iy + 1, iz + 1, seed);
    const x00 = n000 + (n100 - n000) * fx;
    const x10 = n010 + (n110 - n010) * fx;
    const x01 = n001 + (n101 - n001) * fx;
    const x11 = n011 + (n111 - n011) * fx;
    const y0 = x00 + (x10 - x00) * fy;
    const y1 = x01 + (x11 - x01) * fy;
    return y0 + (y1 - y0) * fz;
}

/** Fractal (fBm) value noise, normalised to [0, 1). */
export function fbm3(x: number, y: number, z: number, octaves: number, seed: number, lacunarity = 2.03, gain = 0.5): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    let f = 1;
    for (let o = 0; o < octaves; o++) {
        sum += valueNoise3(x * f, y * f, z * f, seed + o * 1013) * amp;
        norm += amp;
        amp *= gain;
        f *= lacunarity;
    }
    return sum / norm;
}

/**
 * Billowy noise in [0, 1]: |2n − 1| folded value noise, summed over octaves. It has rounded
 * lobes with creases between them, the "cauliflower" structure of cumulus surfaces (the same
 * role the inverted Worley term plays in Perlin-Worley cloud noise).
 */
export function billow3(x: number, y: number, z: number, octaves: number, seed: number): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    let f = 1;
    for (let o = 0; o < octaves; o++) {
        const n = valueNoise3(x * f, y * f, z * f, seed + o * 7919);
        sum += (1 - Math.abs(2 * n - 1)) * amp;
        norm += amp;
        amp *= 0.5;
        f *= 2.17;
    }
    return sum / norm;
}

/** Cellular (Worley) F1 distance in cell units, roughly [0, 1]. */
export function worley3(x: number, y: number, z: number, seed: number): number {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const iz = Math.floor(z);
    let best = 8;
    for (let dz = -1; dz <= 1; dz++) {
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const cx = ix + dx;
                const cy = iy + dy;
                const cz = iz + dz;
                const px = cx + hash3(cx, cy, cz, seed);
                const py = cy + hash3(cx, cy, cz, seed + 1);
                const pz = cz + hash3(cx, cy, cz, seed + 2);
                const d = (px - x) * (px - x) + (py - y) * (py - y) + (pz - z) * (pz - z);
                if (d < best) best = d;
            }
        }
    }
    return Math.min(1, Math.sqrt(best));
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
    const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
}

export function clamp(x: number, lo: number, hi: number): number {
    return x < lo ? lo : x > hi ? hi : x;
}

export function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

/** Standard normal sample (Box-Muller) from a uniform source. */
export function gaussian(rng: () => number): number {
    const u = Math.max(1e-9, rng());
    const v = rng();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
